import type { MutationUpdateProgramArgs, Program, ProgramOrderBy } from '@/graphql/graphql';
import { SortOrder } from '@/graphql/graphql';
import {
    DELETE_PROGRAM,
    GET_EPISODES_PRICE,
    GET_PROGRAM,
    GET_PROGRAMS,
    UPDATE_PROGRAM,
} from '@/graphql/queries/programs.graphql';
import { OperationVariables } from '@apollo/client';
import { apolloClient } from './apollo-client';

export type TransformedProgram = Omit<Program, 'excludedCountries'> & {
    excludedCountries: Record<string, string>;
};

const programssSortFieldMap: Record<string, keyof ProgramOrderBy> = {
    directors: 'directors',
    duration: 'duration',
    episodesQuantity: 'episodesQuantity',
    firstBroadcastDate: 'firstBroadcastDate',
    createdAt: 'createdAt',
    keywords: 'keywords',
    licensesQuantity: 'licensesQuantity',
    parentId: 'parentId',
    price: 'price',
    published: 'published',
    quality: 'quality',
    sku: 'sku',
    synopsis: 'synopsis',
    titles: 'titles',
    validated: 'validated',
    sellerLegalName: 'seller',
};

export type FetchProgramsListArgs = {
    pageSize: number;
    page?: number;
    filters?: {
        [key: string]: unknown;
    };
    sortBy?: string;
    excludedGenres?: string[];
    sortOrder?: 'asc' | 'desc' | 'random';
    random?: boolean;
    admin?: boolean;
    includeSellerData?: boolean;
    includeSensitiveData?: boolean;
    includeMasterFiles?: boolean;
    skipAuth?: boolean;
};

export async function fetchProgramsList({
    page = 1,
    pageSize = 20,
    excludedGenres,
    filters,
    sortBy,
    sortOrder,
    random = false,
    includeSensitiveData = false,
    includeMasterFiles = false,
    includeSellerData = false,
    skipAuth = false,
}: FetchProgramsListArgs) {
    let sortObj: ProgramOrderBy | undefined;
    if (random) {
        sortObj = { random: true } as ProgramOrderBy;
    } else if (sortBy && programssSortFieldMap[sortBy]) {
        sortObj = {} as ProgramOrderBy;
        if (programssSortFieldMap[sortBy] === 'seller') {
            sortObj[programssSortFieldMap[sortBy]] = {
                legalName: sortOrder as SortOrder,
            };
        } else {
            sortObj = {
                [programssSortFieldMap[sortBy]]: sortOrder as SortOrder,
            } as ProgramOrderBy;
        }
    }

    if (filters) {
        const status = filters.status;
        if (status) {
            switch (status) {
                case 'pending':
                    filters.validated = false;
                    filters.published = false;
                    break;
                case 'not_published':
                    filters.validated = true;
                    filters.published = false;
                    break;
                case 'published':
                    filters.validated = true;
                    filters.published = true;
                    break;
            }

            filters.status = undefined;
        }
    }

    let minimumDuration: number | undefined;
    let maximumDuration: number | undefined;
    let minimumEpisodesQuantity: number | undefined;
    let maximumEpisodesQuantity: number | undefined;

    const durationFilter = filters?.duration ? JSON.parse(filters.duration as string) : null;
    if (durationFilter) {
        minimumDuration = durationFilter.min && Number(durationFilter.min);
        maximumDuration = durationFilter.max && Number(durationFilter.max);
    }

    const episodesQuantityFilter = filters?.episodesQuantity ? JSON.parse(filters.episodesQuantity as string) : null;
    if (episodesQuantityFilter) {
        minimumEpisodesQuantity = episodesQuantityFilter.min && Number(episodesQuantityFilter.min);
        maximumEpisodesQuantity = episodesQuantityFilter.max && Number(episodesQuantityFilter.max);
    }

    const response = await apolloClient.query({
        query: GET_PROGRAMS,
        variables: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            orderBy: sortObj,
            audioLanguages: filters?.audioLanguages,
            awards: filters?.awards,
            excludedGenres: excludedGenres,
            categoryIds: filters?.categoryIds,
            coverUrl: filters?.coverUrl,
            directors: filters?.directors,
            genres: filters?.genres,
            keywords: filters?.keywords,
            language: filters?.language,
            maximumDuration: maximumDuration,
            maximumEpisodesQuantity: maximumEpisodesQuantity,
            maximumFirstBroadcastDate: filters?.maximumFirstBroadcastDate,
            maximumLicensesQuantity: filters?.maximumLicensesQuantity,
            maximumPrice: filters?.maximumPrice,
            minimumDuration: minimumDuration,
            minimumEpisodesQuantity: minimumEpisodesQuantity,
            minimumFirstBroadcastDate: filters?.minimumFirstBroadcastDate,
            minimumLicensesQuantity: filters?.minimumLicensesQuantity,
            minimumPrice: filters?.minimumPrice,
            parentId: filters?.parentId,
            productionCountries: filters?.productionCountries,
            quality: filters?.quality,
            published: filters?.published,
            sellerId: filters?.sellerId,
            sellerLegalName: filters?.sellerLegalName,
            sku: filters?.sku && Number(filters?.sku),
            subtitleLanguage: filters?.subtitleLanguage,
            synopsis: filters?.synopsis,
            title: filters?.titles,
            validated: filters?.validated,
            includeSensitiveData,
            includeMasterFiles,
            includeSellerData,
        },
        context: { skipAuth },
    });

    const programs = response.data.searchPrograms.Programs;

    return { data: programs, total: response.data.searchPrograms.count };
}

export async function fetchAllPrograms({
    pageSize = 100,
    filters,
    sortBy,
    sortOrder,
    includeSensitiveData = false,
    includeMasterFiles = false,
}: FetchProgramsListArgs) {
    const firstBatch = await fetchProgramsList({
        page: 1,
        pageSize,
        filters,
        sortBy,
        sortOrder,
        includeSensitiveData,
        includeMasterFiles,
    });

    let data = firstBatch.data;

    if (firstBatch.total > pageSize) {
        let currentPage = 2;
        while ((currentPage - 1) * pageSize < firstBatch.total) {
            const newBatch = await fetchProgramsList({
                page: currentPage,
                pageSize,
                filters,
                sortBy,
                sortOrder,
                includeSensitiveData,
                includeMasterFiles,
            });

            data = data.concat(newBatch.data);

            currentPage++;
        }
    }

    return {
        data: data,
        total: firstBatch.total,
    };
}

export type FetchSubProgramsListArgs = {
    id: string;
    page?: number;
    pageSize?: number;
    filters?: {
        [key: string]: unknown;
    };

    includeSensitiveData?: boolean;
    includeMasterFiles?: boolean;
    skipAuth?: boolean;
};
export async function fetchSubProgramsList({
    page = 1,
    pageSize = 100,
    id,
    filters,
    includeSensitiveData = false,
    includeMasterFiles = false,
    skipAuth = false,
}: FetchSubProgramsListArgs) {
    const response = await apolloClient.query({
        query: GET_PROGRAMS,
        variables: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            parentId: id,
            sellerId: filters?.sellerId,
            includeSensitiveData,
            includeMasterFiles,
            orderBy: {
                episodeNumber: SortOrder.Asc,
            },
        },
        context: { skipAuth },
    });

    const programs = response.data.searchPrograms.Programs;

    return { data: programs, total: response.data.searchPrograms.count };
}

export async function updateProgram(input: MutationUpdateProgramArgs) {
    const variables: MutationUpdateProgramArgs = {
        awards: input.awards || null,
        casting: input.casting || null,
        categoryId: input.categoryId,
        directors: input.directors || null,
        episodeNumber: input.episodeNumber,
        excludedCountries: input.excludedCountries || [],
        firstBroadcastDate: input.firstBroadcastDate,
        genreIds: input.genreIds?.length ? input.genreIds : null,
        id: input.id,
        imdb: input.imdb || null,
        isSegmentable: input.isSegmentable,
        isExclusive: input.isExclusive,
        keywords: input.keywords,
        productionCountry: input.productionCountry,
        synopsis: input.synopsis,
        titles: input.titles,
        published: input.published,
        validated: input.published ? true : input.validated,
    };

    const result = await apolloClient.mutate({
        mutation: UPDATE_PROGRAM,
        variables: variables as OperationVariables,
    });

    return { data: result };
}

export async function updateProgramOrder({ id, episodeNumber }: { id: string; episodeNumber: number }) {
    await apolloClient.mutate({
        mutation: UPDATE_PROGRAM,
        variables: {
            episodeNumber,
            id,
        },
    });

    return true;
}

export async function getProgram({
    id,
    includeSensitiveData = false,
    includeMasterFiles = false,
    skipAuth = false,
}: { id: string; includeSensitiveData?: boolean; includeMasterFiles?: boolean; skipAuth?: boolean }) {
    const response = await apolloClient.query({
        query: GET_PROGRAM,
        variables: {
            id,
            includeSensitiveData,
            includeMasterFiles,
        },
        context: { skipAuth },
    });

    return response.data.program;
}

export async function deleteProgram(id: string) {
    await apolloClient.mutate({
        mutation: DELETE_PROGRAM,
        variables: {
            id: id,
        },
    });
}

export async function getEpisodesPrice(id: string, excludedEpisodeIDs: string[]): Promise<number | null> {
    try {
        const response = await apolloClient.query({
            query: GET_EPISODES_PRICE,
            variables: {
                id: id,
                excludedEpisodeIDs: excludedEpisodeIDs,
            },
        });

        return response.data.programEpisodesPrice ?? 0;
    } catch {
        return null;
    }
}
